import { cn } from '@/lib/utils';
import { retiredEquipmentTypeIdsState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { EquipmentTypeId } from '@rock/types';
import { X } from 'lucide-react';
import type { FC } from 'react';

/**
 * One equipment type, as a blue id chip — the catalog's house style for type references (a type's ID is
 * the stable thing an admin recognizes; the title is only a label on it, shown on hover). `onRemove`
 * turns it into a removable pill. A retired type's chip is grey and says "(retired)" wherever it shows, over
 * any `className`; `idClassName` styles the id alone, so a strike through it leaves "(retired)" readable.
 */
export const EquipmentTypeBadge: FC<{
    typeId: EquipmentTypeId;
    title?: string;
    onRemove?: () => void;
    removeLabel?: string;
    disabled?: boolean;
    className?: string;
    idClassName?: string;
}> = ({ typeId, title, onRemove, removeLabel, disabled, className, idClassName }) => {
    const retired = useIsRetiredType(typeId);
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800',
                className,
                retired && 'bg-muted text-muted-foreground ring-border',
            )}
            title={title}
        >
            <span className={idClassName}>{typeId}</span>
            {retired && <span className="font-normal">(retired)</span>}
            {onRemove && (
                <button
                    type="button"
                    aria-label={removeLabel ?? `Remove type ${typeId}`}
                    onClick={onRemove}
                    disabled={disabled}
                >
                    <X className="h-3 w-3" />
                </button>
            )}
        </span>
    );
};

export function useIsRetiredType(typeId: EquipmentTypeId): boolean {
    return !!useAsyncAtomValue(retiredEquipmentTypeIdsState)?.has(typeId);
}
